// Wording for the Account screen (Settings › Team Sync on iOS: TeamSyncView, AccountActionsCard,
// DeleteAccountButton), with "this iPhone" said as "this browser". Pure (no Supabase client), so it is unit-tested.

import type { Role } from '@/contracts/wire';
import { isNetworkError, LastAdminError, OFFLINE_TEXT, teamErrorMessage } from './errors';

/** Role names as the iOS app shows them (`UserRole.title`). */
export const ROLE_TITLES: Readonly<Record<Role, string>> = { admin: 'Admin', manager: 'Manager', rep: 'Sales Rep' };

/** The account as iOS labels it (`SyncCenter.accountLabel`): its email, else its phone number; null for neither. */
export function accountLabel(user: { email?: string | null; phone?: string | null } | null | undefined): string | null {
  const email = user?.email?.trim();
  if (email) return email;
  const digits = (user?.phone ?? '').replace(/\D/g, '');
  if (digits === '') return null;
  // GoTrue keeps E.164 without the "+". US numbers read like the app shows them: +1 (555) 555-0100.
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return `+${digits}`;
}

export interface ConfirmationText {
  title: string;
  message: string;
  confirmLabel: string;
}

/**
 * Leave company (TeamSyncView.leaveMessage). `onlyMember`: true = leaving deletes the company; false = it stays;
 * null = couldn't be checked, so both cases are explained.
 */
export function leaveConfirmation(onlyMember: boolean | null, company: string): ConfirmationText {
  if (onlyMember === true) {
    return {
      title: `Delete ${company}?`,
      message: `You're the only member of ${company}. Leaving deletes the company and all its records, photos and documents from the team server for good, and removes them from this browser.`,
      confirmLabel: 'Leave and delete the company',
    };
  }
  return {
    title: `Leave ${company}?`,
    message: onlyMember === false
      ? `You lose access to ${company} and its data is removed from this browser. An admin can invite you again later.`
      : `You lose access to ${company} and its data is removed from this browser. If you're its only member, the company and all its data are deleted from the team server for good.`,
    confirmLabel: 'Leave company',
  };
}

/** Delete my account (TeamSyncView.deleteMessage). */
export function deleteConfirmation(onlyMember: boolean | null, company: string, account: string | null): ConfirmationText {
  const base = `Your sign-in (${account ?? 'this account'}) is deleted from the team server and you leave ${company}. This cannot be undone.`;
  const tail = onlyMember === true
    ? ` You're the company's only member, so ${company} and all its data are deleted for good too.`
    : onlyMember === false
      ? " The company's records stay with your team; this browser's copy is removed."
      : " The company's records stay with your team, unless you're its only member: then they're deleted for good.";
  return {
    title: 'Delete your account?',
    message: base + tail,
    confirmLabel: onlyMember === true ? 'Delete my account and the company' : 'Delete my account permanently',
  };
}

/** After a deletion (DeleteAccountButton.deletedTitle / TeamSyncView's message), shown on the sign-in screen. */
export const ACCOUNT_DELETED_NOTICE = {
  title: 'Account deleted',
  message: 'Your Ground Team account was deleted and team data was removed from this browser.',
} as const;

/**
 * When delete-account gets no answer while the browser is online: a dropped connection, or an error the platform
 * answers before the function runs (no CORS headers, so the browser sees no answer). Platform-neutral, since the
 * Android app shows this too: try again, else the public deletion page (in the app, by email). Before the functions are
 * deployed with CORS (WEB_PLAN.md Appendix A item 1) every attempt from the live site ends here.
 */
export const DELETE_NO_ANSWER_TEXT =
  "Couldn't reach Ground Team to delete your account. Try again in a moment. If it keeps failing, see " +
  'groundteam-app.netlify.app/delete-account for other ways to delete it, including by email.';

/**
 * Why "Delete my account" failed. No answer while the browser is online → try again or other ways (see above);
 * offline → the offline text; delete-account's 409 → "You're the only admin of <company>…" (the company it names,
 * else the current one); anything else like iOS `TeamSyncMessages`.
 */
export function deleteAccountErrorMessage(e: unknown, companyName: string | null, online: boolean): string {
  if (isNetworkError(e)) return online ? DELETE_NO_ANSWER_TEXT : OFFLINE_TEXT;
  if (e instanceof LastAdminError) return teamErrorMessage(e, e.company ?? companyName);
  return teamErrorMessage(e, companyName);
}

/** Sign out with edits still waiting after the push (TeamSyncView.signOutMessage, web wording for the buttons). */
export function unsavedChangesMessage(count: number): string {
  const n = Math.max(1, Math.round(count));
  return `${n} ${n === 1 ? "change hasn't" : "changes haven't"} reached the team server yet. Signing out now loses ${n === 1 ? 'it' : 'them'}. Try again when you're online, or sign out anyway.`;
}
